import { motion } from "framer-motion";
import { ArrowUpRight, Play } from "lucide-react";

import { PageHero } from "@/components/layout/PageHero";
import { pages } from "@/content/pages";
import type { Locale } from "@/i18n/locales";

type MediaViewProps = {
  locale: Locale;
};

export function MediaView({ locale }: MediaViewProps) {
  const copy = pages[locale].media;
  const [article, video] = copy.items;

  return (
    <main className="bg-background">
      <PageHero eyebrow={copy.eyebrow} title={copy.title} intro={copy.intro} />

      <section className="pb-28">
        <div className="mx-auto max-w-6xl space-y-6 px-5">
          <motion.article
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.45 }}
            className="overflow-hidden rounded-3xl border border-border/70 bg-card shadow-sm transition-shadow duration-300 hover:shadow-md"
          >
            <a
              href={article.href}
              target="_blank"
              rel="noopener noreferrer"
              className="group grid md:grid-cols-12"
            >
              <div className="aspect-[16/10] overflow-hidden bg-secondary md:col-span-5 md:aspect-auto md:min-h-[280px]">
                <img
                  src="/images/team/marco.webp"
                  alt={article.imgAlt}
                  width={781}
                  height={788}
                  loading="lazy"
                  className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                />
              </div>
              <div className="flex flex-col justify-center p-7 md:col-span-7 md:p-10 lg:p-12">
                <p className="text-xs font-semibold uppercase tracking-widest text-primary">
                  {article.kind}
                </p>
                <h2 className="mt-3 font-heading text-xl font-bold leading-snug tracking-tight text-[#1a3a5c] transition-colors group-hover:text-primary md:text-2xl">
                  {article.title}
                </h2>
                <p className="mt-4 text-sm leading-relaxed text-muted-foreground md:text-base">
                  {article.description}
                </p>
                <span className="mt-6 inline-flex items-center gap-1.5 text-sm font-semibold text-primary">
                  {article.cta}
                  <ArrowUpRight className="h-4 w-4 transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
                </span>
              </div>
            </a>
          </motion.article>

          <motion.article
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.45, delay: 0.08 }}
            className="overflow-hidden rounded-3xl bg-[#1a3a5c] shadow-xl"
          >
            <div className="grid lg:grid-cols-12">
              <div className="relative aspect-video bg-black lg:order-2 lg:col-span-7 lg:aspect-auto lg:h-full lg:min-h-[380px]">
                <iframe
                  src={video.href}
                  title={video.title}
                  className="absolute inset-0 h-full w-full"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              </div>
              <div className="flex flex-col justify-center px-7 py-8 md:px-10 md:py-12 lg:col-span-5 lg:px-12">
                <div className="mb-5 flex items-center gap-2.5">
                  <span className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-white/10">
                    <Play className="h-3.5 w-3.5 fill-white text-white" />
                  </span>
                  <span className="text-xs font-semibold uppercase tracking-widest text-white/70">
                    {video.source}
                  </span>
                </div>
                <h2 className="font-heading text-2xl font-bold leading-snug tracking-tight text-white md:text-3xl">
                  {video.title}
                </h2>
                <p className="mt-4 text-sm leading-relaxed text-white/70 md:text-base">
                  {video.description}
                </p>
              </div>
            </div>
          </motion.article>
        </div>
      </section>
    </main>
  );
}
